import { useMemo } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Dialog } from '@factorialco/f0-react'
import { OneDataCollection, useDataCollectionSource } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import type { RemoveConfirmationData } from './useRemoveTransferFromPayment'

interface TransferRow {
  id: string
  beneficiaryName: string
  amountCents: number
  currency: string
  [key: string]: unknown
}

interface Props {
  data: RemoveConfirmationData
  locale: string
  isLoading: boolean
  onConfirm: () => void
  onClose: () => void
}

export const ConfirmRemoveTransferFromPaymentDialog = ({
  data,
  locale,
  isLoading,
  onConfirm,
  onClose,
}: Props) => {
  const transfers: TransferRow[] = data.transfers

  const source = useDataCollectionSource(
    {
      dataAdapter: {
        fetchData: async () => ({
          records: transfers,
        }),
      },
    },
    [transfers]
  )

  const columns = useMemo(
    () => [
      {
        label: i18n.t('banking.bulkPayments.detail.columns.beneficiary'),
        render: (item: TransferRow) => ({
          type: 'text' as const,
          value: item.beneficiaryName,
        }),
      },
      {
        label: i18n.t('banking.bulkPayments.detail.columns.amount'),
        render: (item: TransferRow) => ({
          type: 'text' as const,
          value: MoneyWithCurrency({
            amount: item.amountCents,
            currency: item.currency,
            locale,
          }),
        }),
      },
    ],
    [locale]
  )

  const visualizations = useMemo(
    () => [
      {
        type: 'table' as const,
        options: {
          columns,
        },
      },
    ],
    [columns]
  )

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('banking.bulkPayments.detail.removeTransferModal.title')}
      width="lg"
      primaryAction={{
        label: i18n.t('banking.bulkPayments.detail.removeTransferModal.confirm'),
        onClick: onConfirm,
        disabled: isLoading,
        loading: isLoading,
        variant: 'critical',
      }}
      secondaryAction={{
        label: i18n.t('banking.bulkPayments.detail.removeTransferModal.cancel'),
        onClick: onClose,
        disabled: isLoading,
      }}
    >
      <Box flexDirection="column" gap="s16">
        <F0Alert
          variant="warning"
          title={
            transfers.length > 1
              ? i18n.t('banking.bulkPayments.detail.removeTransferModal.description', {
                  label: data.paymentRequestLabel,
                })
              : i18n.t('banking.bulkPayments.detail.removeTransferModal.descriptionSingle', {
                  label: data.paymentRequestLabel,
                })
          }
          description={i18n.t('banking.bulkPayments.detail.removeTransferModal.reassignment')}
        />

        {transfers.length > 0 && (
          <Box style={{ maxHeight: '400px', overflow: 'auto' }}>
            <OneDataCollection source={source} visualizations={visualizations} />
          </Box>
        )}
      </Box>
    </F0Dialog>
  )
}
